import { useEffect, useRef, useState } from 'react';
import { ScoreRing } from '@/components/ui/ScoreRing';
import { ScoreSubBars } from '@/components/ui/ScoreSubBars';
import { cx } from '@/cva.config';
import type { ScoreBreakdown } from '@/lib/score';
import { scoreTier } from '@/lib/score';

interface PopoverCoords {
  left: number;
  top: number;
}

interface ScoreWithTooltipProps {
  breakdown?: ScoreBreakdown;
  disabled?: boolean;
  placement?: 'left' | 'right';
  score: number | null;
  size?: 'sm' | 'md' | 'lg';
  style?: 'subbars' | 'ring';
}

const POPOVER_WIDTH = 256;
const POPOVER_HEIGHT_ESTIMATE = 260;
const POPOVER_GAP = 10;
const VIEWPORT_MARGIN = 8;

const SUB_BREAKDOWN_ROWS: Array<{
  label: string;
  key: keyof Omit<ScoreBreakdown, 'explanation'>;
}> = [
  { key: 'roleFit', label: 'Role fit' },
  { key: 'techMatch', label: 'Tech match' },
  { key: 'comp', label: 'Compensation' },
  { key: 'logistics', label: 'Logistics' },
];

const subBarColor = (percentage: number) =>
  percentage >= 75
    ? 'bg-score-high'
    : percentage >= 50
      ? 'bg-score-mid'
      : 'bg-score-low';

export const ScoreWithTooltip = ({
  score,
  breakdown,
  placement = 'right',
  disabled = false,
  style = 'subbars',
  size = 'md',
}: ScoreWithTooltipProps) => {
  const [open, setOpen] = useState(false);
  const [clickedOpen, setClickedOpen] = useState(false);
  const [popoverCoords, setPopoverCoords] = useState<PopoverCoords | null>(
    null,
  );
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const enterTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const leaveTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(
    () => () => {
      clearTimeout(enterTimeout.current);
      clearTimeout(leaveTimeout.current);
    },
    [],
  );

  const computePopoverCoords = () => {
    if (!triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();

    const rightSpace = window.innerWidth - triggerRect.right;
    const shouldFlipLeft =
      placement === 'left'
      || (placement === 'right'
        && rightSpace < POPOVER_WIDTH + POPOVER_GAP + VIEWPORT_MARGIN);

    const left = shouldFlipLeft
      ? Math.max(
          VIEWPORT_MARGIN,
          triggerRect.left - POPOVER_WIDTH - POPOVER_GAP,
        )
      : Math.min(
          window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN,
          triggerRect.right + POPOVER_GAP,
        );

    const wantedTop =
      triggerRect.top + triggerRect.height / 2 - POPOVER_HEIGHT_ESTIMATE / 2;
    const top = Math.max(
      VIEWPORT_MARGIN,
      Math.min(
        window.innerHeight - POPOVER_HEIGHT_ESTIMATE - VIEWPORT_MARGIN,
        wantedTop,
      ),
    );

    setPopoverCoords({ left, top });
  };

  const handleEnter = () => {
    clearTimeout(leaveTimeout.current);

    if (clickedOpen) {
      return;
    }

    computePopoverCoords();
    enterTimeout.current = setTimeout(() => setOpen(true), 180);
  };

  const handleLeave = () => {
    clearTimeout(enterTimeout.current);

    if (clickedOpen) {
      return;
    }

    leaveTimeout.current = setTimeout(() => setOpen(false), 120);
  };

  const handleClick = () => {
    setOpen((previous) => !previous);
    setClickedOpen((previous) => !previous);
    computePopoverCoords();
  };

  const tier = scoreTier(score);

  if (disabled) {
    return style === 'ring' ? (
      <ScoreRing score={score} size={size} tier={tier} />
    ) : (
      <ScoreSubBars breakdown={breakdown} score={score} />
    );
  }

  return (
    <div
      className={cx(
        'relative inline-flex',
        style === 'ring' ? 'shrink-0' : 'w-full',
      )}
      onClick={handleClick}
      onKeyDown={handleClick}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      ref={triggerRef}
      role="tooltip"
    >
      {style === 'ring' ? (
        <ScoreRing score={score} size={size} tier={tier} />
      ) : (
        <ScoreSubBars breakdown={breakdown} score={score} />
      )}
      {open && breakdown && popoverCoords && (
        <span
          className="pointer-events-none fixed z-100 w-96 animate-fadeIn rounded-md border border-border-active bg-surface shadow-2xl"
          role="tooltip"
          style={{ left: popoverCoords.left, top: popoverCoords.top }}
        >
          <span className="block border-b border-border px-3 pt-2.5 pb-2">
            <span className="flex items-baseline gap-2">
              <span
                className={cx(
                  'font-mono text-sm lg:text-base font-semibold tabular-nums',
                  {
                    'text-foreground-muted': tier === 'none',
                    'text-score-high': tier === 'high',
                    'text-score-low': tier === 'low',
                    'text-score-mid': tier === 'mid',
                  },
                )}
              >
                {score ?? '–'}
              </span>
              <span className="font-mono text-2xs lg:text-xs tracking-wider text-subtle uppercase">
                / 100 · {tier} match
              </span>
            </span>
          </span>
          <span className="block my-1 px-3 py-2">
            {SUB_BREAKDOWN_ROWS.map(({ label, key }) => {
              const value = breakdown[key];
              const percentage = (value / 25) * 100;
              const barColor = subBarColor(percentage);

              return (
                <span
                  className="flex items-center gap-2 text-xs lg:text-sm"
                  key={label}
                >
                  <span className="min-w-26.25 shrink-0 text-foreground-muted">
                    {label}
                  </span>
                  <span className="block h-1 flex-1 overflow-hidden rounded-full bg-border">
                    <span
                      className={cx('block h-full rounded-full', barColor)}
                      style={{ width: `${percentage}%` }}
                    />
                  </span>
                  <span className=" shrink-0 text-right font-mono text-foreground tabular-nums">
                    {value}/25
                  </span>
                </span>
              );
            })}
          </span>
          {breakdown.explanation && (
            <span className="block border-t border-border px-3 py-2 text-2xs/snug lg:text-xs/snug text-balance text-foreground-muted">
              {breakdown.explanation.length > 140
                ? `${breakdown.explanation.slice(0, 140)}…`
                : breakdown.explanation}
            </span>
          )}
        </span>
      )}
    </div>
  );
};
